import Box from "@mui/material/Box";
import NodeSkeleton from "./NodeSkeleton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { type NodeProps } from "@xyflow/react";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import NodeListCard from "./NodeListCard";
import NodeListRow, { NodeRowChevron } from "./NodeListRow";
import NodeToolbar from "./NodeToolbar";

function SalesOrderLinesNode({ data }: NodeProps<ExplorerNode>) {
    const lines = data.lines ?? [];
    const isLoading = data.status === "loading";
    const nodeColor = getNodeColor(data.entityType);

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            width={240}
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                dataAreaId={data.dataAreaId}
                onInfoClick={data.onSelect ? () => data.onSelect!(data.selectId ?? data.entityType) : undefined}
            />

            {isLoading ? (
                <NodeSkeleton rows={4} variant="list" />
            ) : lines.length === 0 ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    No lines found
                </Typography>
            ) : (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {lines.map((line, index) => (
                        <React.Fragment key={line.lineKey ?? line.lineNumber}>
                            {index > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                            <NodeListRow
                                nodeColor={nodeColor}
                                isExpanded={line.isExpanded}
                                onClick={() => {
                                    if (data.onLineKeySelect && line.lineKey) {
                                        data.onLineKeySelect(line.lineKey);
                                    } else {
                                        data.onLineSelect?.(line.lineNumber);
                                    }
                                }}
                            >
                                <Tooltip title={line.lineDescription || ""} arrow disableHoverListener={!line.lineDescription}>
                                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 400,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {line.externalItemNumber || line.itemNumber}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <NodeRowChevron isExpanded={line.isExpanded} nodeColor={nodeColor} />
                                    </Stack>
                                </Tooltip>
                            </NodeListRow>
                        </React.Fragment>
                    ))}
                </List>
            )}
        </NodeListCard>
    );
}

export default memo(SalesOrderLinesNode);
